import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import { inputVariants } from "@react/shared/ui/Input"
import { t, fmtPeriod, fmtMonth } from "../helpers/format"
import { STATES } from "../helpers/states"

const Segmented = ({ value, options, onChange }) => (
  <div className="inline-flex p-0.5 rounded-md bg-slate-100 border border-slate-200">
    {options.map(option => (
      <button
        key       = {option.value}
        type      = "button"
        onClick   = {() => onChange(option.value)}
        className = {clsx(
          "h-7 px-3 rounded-[5px] text-[12.5px] font-medium transition-colors",
          value === option.value ? "bg-white text-slate-900 ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
)

const FilterSelect = ({ icon, value, onChange, placeholder, options, width }) => (
  <div className={clsx("relative", width)}>
    <FontAwesomeIcon icon={icon} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none" />
    <select
      value     = {value || ""}
      onChange  = {e => onChange(e.target.value)}
      className = {clsx(inputVariants({ size: "sm" }), "!pl-7 truncate", value ? "!border-brand-300 bg-brand-50/40" : "")}
    >
      <option value="">{placeholder}</option>
      {options.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
    </select>
  </div>
)

const Toolbar = ({ board, filters, mode, loading, signedCount, busy, onMode, onPrev, onNext, onToday, onFilter, onApproveSigned }) => {
  const statusOptions = STATES.map(state => ({ id: state, name: t(`states.${state}`) }))

  return (
    <div className="mx-7 mb-4 flex flex-wrap items-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-xl">
      <div className="flex items-center gap-1">
        <IconButton size="md" onClick={onPrev} aria-label={t("period.previous")} title={t("period.previous")}>
          <FontAwesomeIcon icon="chevron-left" />
        </IconButton>
        <Button variant="ghost" size="sm" onClick={onToday}>{t("period.today")}</Button>
        <IconButton size="md" onClick={onNext} aria-label={t("period.next")} title={t("period.next")}>
          <FontAwesomeIcon icon="chevron-right" />
        </IconButton>
        <div className="ml-1 min-w-[150px] text-[13.5px] font-semibold text-slate-900 tabular-nums first-letter:uppercase">
          {mode === "month" ? fmtMonth(filters.from) : fmtPeriod(filters.from, filters.to)}
        </div>
      </div>

      <Segmented
        value    = {mode}
        onChange = {onMode}
        options  = {[{ value: "week", label: t("period.week") }, { value: "month", label: t("period.month") }]}
      />

      <div className="h-5 w-px bg-slate-200 mx-1" />

      <FilterSelect
        icon        = "users"
        width       = "w-[180px]"
        value       = {filters.manager_id}
        placeholder = {t("filters.all_teams")}
        options     = {board.managers || []}
        onChange    = {value => onFilter({ manager_id: value })}
      />
      <FilterSelect
        icon        = "helmet-safety"
        width       = "w-[220px]"
        value       = {filters.project_id}
        placeholder = {t("filters.all_projects")}
        options     = {board.projects || []}
        onChange    = {value => onFilter({ project_id: value })}
      />
      <FilterSelect
        icon        = "filter"
        width       = "w-[160px]"
        value       = {filters.status}
        placeholder = {t("filters.all_states")}
        options     = {statusOptions}
        onChange    = {value => onFilter({ status: value })}
      />

      {loading && (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 ml-1">
          <FontAwesomeIcon icon="circle-notch" spin />
          {t("loading")}
        </span>
      )}

      <div className="ml-auto">
        <Button
          variant  = "primary"
          size     = "sm"
          disabled = {!signedCount || busy}
          onClick  = {onApproveSigned}
          title    = {signedCount ? null : t("actions.no_signed")}
        >
          <FontAwesomeIcon icon="check-double" />
          {t("actions.approve_signed")}
          {signedCount > 0 && (
            <span className="ml-0.5 px-1.5 rounded-full bg-white/25 text-[11px] tabular-nums">{signedCount}</span>
          )}
        </Button>
      </div>
    </div>
  )
}

export default Toolbar
